import { NextSeo } from 'next-seo';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { GetServerSideProps, NextPage } from 'next';
import { useTranslations } from 'next-intl';
import { injectLayoutData } from '@utils/layout';
import { PrismicDocument } from '@prismicio/client';
import { prismicService, searchClient } from '@services/businessContainer';
import { formatPrismicData } from '@monofront/business/utils';
import SharedWishlist from '@screens/SharedWishlist';
import { IPrismicImage } from '@monofront/types';
import { injectGetServerSidePropsHeaders } from '@utils/query';
import {
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-core';
import { useInstantSearchNextRouting } from '@hooks/useInstantSearchNextRouting';
import {
  InstantSearchSSRProps,
  renderInstantSearchSSR,
} from '@utils/renderInstantSearchSSR';

const {
  publicRuntimeConfig: { appBaseUrl },
} = getConfig();

type PageSharedWishlistsProps = InstantSearchSSRProps & {
  pageContent: ISharedWishlistPageContent;
  title: string | null;
  owner: string | null;
  skus: Array<string>;
};

export interface ISharedWishlistPageContent {
  addToCartButtonLabel: string;
  shareMyListBannerImage: IPrismicImage;
}

const NextSeoWishlist = () => {
  const router = useRouter();

  const tWishlists = useTranslations('Wishlists');

  return (
    <NextSeo
      title={tWishlists('myLists')}
      canonical={`${appBaseUrl}${router.pathname}`}
      noindex={true}
      nofollow={false}
      openGraph={{
        url: `${appBaseUrl}${router.pathname}`,
        title: tWishlists('myLists'),
      }}
    />
  );
};

const PageWishlist: NextPage<PageSharedWishlistsProps> = ({
  pageContent,
  owner,
  title,
  skus,
  baseIndexName,
  searchServerState,
  serverUrl,
}) => {
  const instantSearchRouting = useInstantSearchNextRouting(
    baseIndexName,
    serverUrl,
  );

  return (
    <InstantSearchSSRProvider {...searchServerState}>
      <InstantSearch
        indexName={baseIndexName}
        searchClient={searchClient}
        routing={instantSearchRouting}
        insights
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        {searchServerState && <NextSeoWishlist />}
        <SharedWishlist
          pageContent={pageContent}
          owner={owner}
          title={title}
          skus={skus}
        />
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

export const getServerSideProps =
  injectGetServerSidePropsHeaders<PageSharedWishlistsProps>(
    injectLayoutData<
      PageSharedWishlistsProps,
      GetServerSideProps<PageSharedWishlistsProps>
    >(async ({ req, previewData, locale, query }) => {
      if (!locale) {
        return {
          notFound: true,
        };
      }

      const title =
        (query.title && decodeURIComponent(query.title as string)) ?? null;
      const owner =
        (query.owner && decodeURIComponent(query.owner as string)) ?? null;
      const skus = (query.skus || []) as string[];

      const results: PrismicDocument = await prismicService.getDocumentByUid(
        'shopwishlist',
        'mes-listes',
        previewData,
        {
          ...(locale ? { lang: `${locale}-${locale}` } : {}),
        },
      );
      const pageContent: ISharedWishlistPageContent = results?.data
        ? formatPrismicData(results.data)
        : null;

      const { baseIndexName, serverUrl, searchServerState } =
        await renderInstantSearchSSR({ req, locale }, PageWishlist, {
          pageContent,
          title,
          owner,
          skus,
        });

      return {
        props: {
          pageContent,
          title,
          owner,
          skus,
          searchServerState,
          baseIndexName,
          serverUrl,
        },
      };
    }),
  );

export default PageWishlist;
